import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiTrash2, FiDownload, FiDollarSign, FiPrinter } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import { invoiceToPDF } from '../../utils/export';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import StatusBadge from '../../components/common/StatusBadge';
import SearchableSelect from '../../components/common/SearchableSelect';
import { formatMoney, formatDate, fmtDate, today } from '../../utils/format';
import { COMPANY_NAME } from '../../config';

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [payModal, setPayModal] = useState(false);
  const [payment, setPayment] = useState({
    payment_date: today(),
    payment_type: 'cash',
    amount: '',
    cheque_no: '',
    bank_account_id: '',
    note: '',
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invoiceRes, settingsRes] = await Promise.all([
        api.invoices.get(id),
        api.settings.get().catch(() => ({ data: {} })),
      ]);
      setInv(invoiceRes.data.invoice || invoiceRes.data);
      setSummary(invoiceRes.data.summary || {});
      const sRaw = settingsRes.data?.settings || settingsRes.data || {};
      const flat = {};
      if (typeof sRaw === 'object' && !Array.isArray(sRaw)) {
        Object.values(sRaw).forEach((group) => {
          if (Array.isArray(group)) group.forEach((s) => { flat[s.key] = s.value; });
        });
      }
      setSettings(flat);
    } catch (err) {
      showError('Failed to load invoice', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async () => {
    try {
      await api.invoices.remove(id);
      toast('success', '', 'Invoice deleted');
      navigate('/invoices');
    } catch (err) {
      showError('Failed to delete invoice', extractError(err));
    }
  };

  const handlePayment = async () => {
    const errs = {};
    if (!payment.payment_date) errs.payment_date = 'Payment date is required';
    if (!payment.payment_type) errs.payment_type = 'Payment type is required';
    if (!payment.amount || Number(payment.amount) <= 0) errs.amount = 'Enter an amount greater than 0';
    if (payment.payment_type === 'bank' && !payment.cheque_no) errs.cheque_no = 'Cheque no is required';
    if (!payment.note) errs.note = 'Note is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      await api.invoices.payments.create(id, {
        ...payment,
        amount: Number(payment.amount),
        bank_account_id: payment.bank_account_id ? Number(payment.bank_account_id) : undefined,
        cheque_no: payment.cheque_no || undefined,
      });
      toast('success', '', 'Payment recorded successfully');
      setPayModal(false);
      setErrors({});
      setPayment({ payment_date: today(), payment_type: 'cash', amount: '', cheque_no: '', bank_account_id: '', note: '' });
      load();
    } catch (err) {
      showError('Failed to record payment', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async () => { await invoiceToPDF({ ...inv, summary }, COMPANY_NAME, settings); };
  const printPDF = async () => { await invoiceToPDF({ ...inv, summary }, COMPANY_NAME, settings, true); };

  if (loading) return <Loader />;
  if (!inv) return null;

  const items = Array.isArray(inv.items) ? inv.items : (inv.items && inv.items.data) || [];
  const payments = Array.isArray(inv.payments) ? inv.payments : (inv.payments && inv.payments.data) || [];
  const displayNo = inv.invoice_type === 'purchase' ? (inv.supplier_invoice_no || inv.invoice_no) : inv.invoice_no;
  const commissionParty = inv.party || {};
  const totalPkr = Number(inv.total_pkr) || 0;

  return (
    <div className="page">
      <PageHeader
        title={displayNo}
        subtitle="Invoice details, status and payments."
        crumbs={[{ label: 'Finance', to: '/invoices' }, { label: 'Invoices', to: '/invoices' }, { label: displayNo }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/invoices')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={downloadPDF}><FiDownload /> Download PDF</button>
            <button className="btn btn-secondary" onClick={printPDF}><FiPrinter /> Print</button>
            <button className="btn btn-primary" onClick={() => setPayModal(true)} disabled={summary.total > 0 && summary.remaining <= 0} style={summary.total > 0 && summary.remaining <= 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}><FiDollarSign /> Receive Payment</button>
            <button className="btn btn-danger-outline" onClick={handleDelete}><FiTrash2 /> Delete</button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          {(inv.invoice_type === 'commission' || inv.invoice_type === 'tax_free') && (settings.commission_header_name || settings.commission_header_phone || settings.commission_header_address || settings.commission_header_email) && (
            <div className="commission-header" style={{ marginBottom: 20 }}>
              <div className="commission-title">{settings.commission_header_name || ''}</div>
              {(settings.commission_header_address || settings.commission_header_phone || settings.commission_header_email) && (
                <div className="commission-subtitle">
                  {settings.commission_header_address ? `Address: ${settings.commission_header_address}` : ''}
                  {settings.commission_header_phone ? `, Tel # ${settings.commission_header_phone}` : ''}
                  {settings.commission_header_email ? `, Email: ${settings.commission_header_email}` : ''}
                </div>
              )}
            </div>
          )}
          {inv.invoice_type === 'commission' ? (
            <>
              <div className="detail-grid">
                <div className="detail-item"><div className="d-label">Type</div><div className="d-value"><StatusBadge status={inv.invoice_type} /></div></div>
                <div className="detail-item"><div className="d-label">Status</div><div className="d-value">
                  {summary.total > 0 && summary.remaining <= 0
                    ? <span className="badge badge-green">Paid</span>
                    : (summary.paid > 0
                      ? <span className="badge badge-blue">Partial Paid</span>
                      : <span className="badge badge-red">Unpaid</span>)}
                </div></div>
                <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Company</div><div className="d-value">{commissionParty.name || '-'}</div></div>
                {(inv.supplier_address || commissionParty.address) && (
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Address</div><div className="d-value">{inv.supplier_address || commissionParty.address}</div></div>
                )}
                {inv.supplier && inv.supplier.name && (
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Supplier</div><div className="d-value">{inv.supplier.name}</div></div>
                )}
                <div className="detail-item"><div className="d-label">Invoice #</div><div className="d-value">{inv.invoice_no || '-'}</div></div>
                <div className="detail-item"><div className="d-label">Date</div><div className="d-value">{fmtDate(inv.date)}</div></div>
                <div className="detail-item"><div className="d-label">Sales Contract</div><div className="d-value">{inv.sales_contract || '-'}</div></div>
                <div className="detail-item"><div className="d-label">Container #</div><div className="d-value">{inv.container_no || '-'}</div></div>
                <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Description / Purpose</div><div className="d-value" style={{ textTransform: 'uppercase' }}>{inv.purpose || 'Commission'}</div></div>
                <div className="detail-item"><div className="d-label">Net Weight (Kg)</div><div className="d-value">{inv.net_weight || '-'}</div></div>
                <div className="detail-item"><div className="d-label">US Dollar Rate</div><div className="d-value">{inv.usd_rate ? `PKR ${inv.usd_rate}` : '-'}</div></div>
                <div className="detail-item"><div className="d-label">Invoice Amount in US$</div><div className="d-value">{inv.invoice_amount_usd ? formatMoney(inv.invoice_amount_usd) : '-'}</div></div>
              </div>
              <div style={{ background: '#ED1B24', color: '#fff', borderRadius: 6, padding: '14px 18px', marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b style={{ fontSize: 13, letterSpacing: 0.6 }}>TOTAL PKR</b>
                <b style={{ fontSize: 18 }}>{totalPkr ? `PKR ${formatMoney(totalPkr)}` : '-'}</b>
              </div>
            </>
          ) : (
            <div className="detail-grid">
              <div className="detail-item"><div className="d-label">Invoice No</div><div className="d-value">{displayNo}</div></div>
              <div className="detail-item"><div className="d-label">Type</div><div className="d-value"><StatusBadge status={inv.invoice_type} /></div></div>
              <div className="detail-item"><div className="d-label">Status</div><div className="d-value">
                {summary.total > 0 && summary.remaining <= 0
                  ? <span className="badge badge-green">Paid</span>
                  : (summary.paid > 0
                    ? <span className="badge badge-blue">Partial Paid</span>
                    : <span className="badge badge-red">Unpaid</span>)}
              </div></div>
              <div className="detail-item"><div className="d-label">Date</div><div className="d-value">{formatDate(inv.date)}</div></div>
              <div className="detail-item"><div className="d-label">{inv.invoice_type === 'purchase' ? 'Supplier' : 'Party'}</div><div className="d-value">{inv.invoice_type === 'purchase' ? (inv.supplier ? inv.supplier.name : '-') : (inv.party ? inv.party.name : '-')}</div></div>
              {!(inv.invoice_type === 'purchase' && Number(inv.tax_rate) === 0) && (
                <div className="detail-item"><div className="d-label">Tax Rate</div><div className="d-value">{inv.tax_rate}%</div></div>
              )}
              {inv.invoice_type === 'sale' && inv.hs_code && <div className="detail-item"><div className="d-label">HS Code</div><div className="d-value">{inv.hs_code}</div></div>}
            </div>
          )}
        </div>
      </div>

      {summary.total > 0 && (
        <div className="stats-grid" style={{ marginTop: 18 }}>
          <div className="stat-card">
            <div className="stat-icon indigo"><FiDollarSign /></div>
            <div><div className="stat-value">{formatMoney(summary.total)}</div><div className="stat-label">Total</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green"><FiDollarSign /></div>
            <div><div className="stat-value">{formatMoney(summary.paid)}</div><div className="stat-label">Paid</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange"><FiDollarSign /></div>
            <div><div className="stat-value">{formatMoney(summary.remaining)}</div><div className="stat-label">Remaining</div></div>
          </div>
          <div className="stat-card">
            <div className="stat-icon blue"><FiDollarSign /></div>
            <div><div className="stat-value">{summary.payment_percentage}%</div><div className="stat-label">Payment %</div></div>
          </div>
        </div>
      )}

      {inv.invoice_type !== 'commission' && (
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Items</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th><th>Description</th><th className="table-num">Qty</th>
                <th>UOM</th>
                <th className="table-num">Unit Price</th><th className="table-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td style={{ textTransform: 'uppercase' }}>{it.description}</td>
                  <td className="table-num">{it.quantity}</td>
                  <td>{it.uom || 'pcs'}</td>
                  <td className="table-num">{formatMoney(it.unit_price)}</td>
                  <td className="table-num">{formatMoney((it.quantity || 0) * (it.unit_price || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span style={{ color: 'var(--text-muted)' }}>Subtotal</span><b>{formatMoney(inv.subtotal)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}><span style={{ color: 'var(--text-muted)' }}>Tax</span><b>{formatMoney(inv.tax_amount)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: 16 }}><b>Total</b><b style={{ color: 'var(--primary)' }}>{formatMoney(inv.total)}</b></div>
          </div>
        </div>
      </div>
      )}

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Payments</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                        <th>#</th><th>Date</th><th>Type</th><th className="table-num">Amount</th>
                        <th className="table-num">Net</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.id}>
                  <td>{i + 1}</td>
                  <td>{formatDate(p.payment_date)}</td>
                  <td><StatusBadge status={p.payment_type} /></td>
                        <td className="table-num">{formatMoney(p.amount)}</td>
                        <td className="table-num">{formatMoney(p.net_amount)}</td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No payments recorded</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={payModal}
        onClose={() => { setPayModal(false); setErrors({}); }}
        title="Receive Payment"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setPayModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handlePayment} disabled={saving}>{saving ? 'Saving...' : 'Record Payment'}</button>
          </>
        }
      >
        <div className="grid-2">
          <div className="form-group">
            <label className="form-label">Payment Date <span className="req">*</span></label>
            <input className={`form-control ${errors.payment_date ? 'is-invalid' : ''}`} type="date" value={payment.payment_date} onChange={(e) => { setPayment({ ...payment, payment_date: e.target.value }); if (errors.payment_date) setErrors((p) => ({ ...p, payment_date: '' })); }} />
            {errors.payment_date && <div className="invalid-feedback">{errors.payment_date}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Payment Type <span className="req">*</span></label>
            <SearchableSelect options={[{ value: '', label: '-- Select payment type --' }, { value: 'cash', label: 'Cash' }, { value: 'bank', label: 'Bank' }]} value={payment.payment_type} onChange={(v) => { setPayment({ ...payment, payment_type: v }); if (errors.payment_type) setErrors((p) => ({ ...p, payment_type: '' })); }} isInvalid={!!errors.payment_type} placeholder="-- Select payment type --" />
            {errors.payment_type && <div className="invalid-feedback">{errors.payment_type}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Amount <span className="req">*</span></label>
            <input className={`form-control ${errors.amount ? 'is-invalid' : ''}`} type="number" value={payment.amount} onChange={(e) => { setPayment({ ...payment, amount: e.target.value }); if (errors.amount) setErrors((p) => ({ ...p, amount: '' })); }} placeholder="Gross amount" />
            {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
          </div>
          {payment.payment_type === 'bank' && (
            <div className="form-group">
              <label className="form-label">Cheque No <span className="req">*</span></label>
              <input className={`form-control ${errors.cheque_no ? 'is-invalid' : ''}`} value={payment.cheque_no} onChange={(e) => { setPayment({ ...payment, cheque_no: e.target.value }); if (errors.cheque_no) setErrors((p) => ({ ...p, cheque_no: '' })); }} />
              {errors.cheque_no && <div className="invalid-feedback">{errors.cheque_no}</div>}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Note <span className="req">*</span></label>
            <input className={`form-control ${errors.note ? 'is-invalid' : ''}`} value={payment.note} onChange={(e) => { setPayment({ ...payment, note: e.target.value }); if (errors.note) setErrors((p) => ({ ...p, note: '' })); }} />
            {errors.note && <div className="invalid-feedback">{errors.note}</div>}
          </div>
        </div>
        {Number(payment.amount) > 0 && (
          <div className="alert-banner info">
            Net received: {formatMoney(Number(payment.amount))}
          </div>
        )}
      </Modal>
    </div>
  );
}